import { useState, useEffect, useMemo, type ReactNode, type CSSProperties } from 'react';
import {
  collection, onSnapshot, doc, runTransaction, serverTimestamp,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { Button, Modal, ConfirmModal, Dropdown } from '../components/ui';
import PurchaseReturnModal from '../components/PurchaseReturnModal';
import { HeaderSearch, DateRangeButton, StatChips, IconPlusSm, IconExportSm } from '../components/HeaderControls';
import { useSettings } from '../hooks/useSettings';
import { downloadCSV } from '../utils/csv';
import { fmtMoney } from '../utils/currency';
import { computeDateWindow, inWindow, type DateFilterKey } from '../utils/ledgerReports';
import { entryId } from '../utils/ledger';
import { findReturnHistoryRefs } from '../utils/returnHistory';
import ReturnHistoryCleanup from '../components/ReturnHistoryCleanup';
import type { PurchaseReturn, PurchaseInvoice, Entity, InventoryItem, PaymentStatus } from '../types';
import { returnCreditOf } from '../utils/tradingSystem';

function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const fmtDate = (s?: string) =>
  s ? new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
// "5 FEB, 2026" — used only in the invoice picker below, per its own spec.
const fmtDateDMY = (s?: string) => {
  if (!s) return '—';
  const d = new Date(s + 'T00:00:00');
  const month = d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
  return `${d.getDate()} ${month}, ${d.getFullYear()}`;
};
// A code is stored in full ("PC-2026-0001"), but every list only shows the
// prefix + last 4 digits to stay compact — same convention as Purchase.tsx's
// lotNoDisplay. The full value is always in a `title` tooltip next to it.
function codeDisplay(code?: string) {
  if (!code) return '—';
  const m = code.match(/^([A-Za-z]+)-\d+-(\d+)$/);
  return m ? `${m[1]}-${m[2].slice(-4)}` : code.slice(-4);
}
function IdPill({ children }: { children: ReactNode }) {
  return (
    <span style={{
      display: 'inline-block', fontFamily: 'var(--font-num)', background: 'var(--primary-light)',
      color: 'var(--primary)', fontSize: 11, fontWeight: 700, padding: '3px 8px',
      borderRadius: 6, letterSpacing: '0.02em',
    }}>
      {children}
    </span>
  );
}
function IconSearch() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M11 11l3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
function IconTrash() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <path d="M2 3.5h10M5.5 3.5V2.5h3v1M4 3.5l.667 8h4.666L10 3.5"
        stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const PAGE_SIZE = 50;

// ── Pagination (same pattern as Purchase/Sales/Inventory/Entity/Memos/AP/AR/SalesReturns) ──
function Pagination({ current, total, totalItems, pageSize, onChange }: {
  current: number; total: number; totalItems: number; pageSize: number; onChange: (p: number) => void;
}) {
  if (total <= 1) return null;
  const from = (current - 1) * pageSize + 1;
  const to   = Math.min(current * pageSize, totalItems);
  const pages: (number | 'e')[] = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3)         pages.push('e');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
    if (current < total - 2) pages.push('e');
    pages.push(total);
  }
  return (
    <div className="pagination">
      <span className="pagination-info">
        Showing <strong>{from}–{to}</strong> of <strong>{totalItems}</strong> return{totalItems !== 1 ? 's' : ''}
      </span>
      <div className="pagination-btns">
        <button className="pg-btn" disabled={current === 1} onClick={() => onChange(current - 1)}>‹</button>
        {pages.map((p, i) => p === 'e'
          ? <span key={`e${i}`} style={{ minWidth: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--text-3)' }}>…</span>
          : <button key={p} className={`pg-btn${p === current ? ' active' : ''}`} onClick={() => onChange(p)}>{p}</button>
        )}
        <button className="pg-btn" disabled={current === total} onClick={() => onChange(current + 1)}>›</button>
      </div>
    </div>
  );
}

// ── Invoice picker (step 1 of "New Return") ───────────────────────────────
function PickInvoiceModal({ invoices, entityName, onPick, onClose }: {
  invoices: PurchaseInvoice[]; entityName: (id: string) => string;
  onPick: (inv: PurchaseInvoice) => void; onClose: () => void;
}) {
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);
  const [q, setQ] = useState('');
  const rows = invoices
    .filter(i => !q || i.invoiceNo.toLowerCase().includes(q.toLowerCase()) || entityName(i.fromEntityId).toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (b.purchaseDate ?? '').localeCompare(a.purchaseDate ?? ''))
    .slice(0, 40);
  return (
    <Modal open onClose={onClose} size="2xl" title="Return against which purchase?"
      footer={<Button variant="secondary" onClick={onClose}>Cancel</Button>}>
      <div style={{ position: 'relative', marginBottom: 12 }}>
        <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none', display: 'flex' }}>
          <IconSearch />
        </span>
        <input
          value={q} onChange={e => setQ(e.target.value)}
          placeholder="Search invoice no or supplier…" autoFocus
          style={{
            width: '100%', paddingLeft: 32, paddingRight: q ? 30 : 10,
            height: 32, fontSize: 12.5, border: '1.5px solid var(--border)',
            borderRadius: 'var(--r-sm)', background: 'var(--surface-2)',
            color: 'var(--text-1)', outline: 'none', boxSizing: 'border-box',
            transition: 'border-color 0.15s, box-shadow 0.15s, background 0.15s',
          }}
          onFocus={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.background = 'var(--surface)'; }}
          onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--surface-2)'; }}
        />
        {q && (
          <button onClick={() => setQ('')} style={{
            position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)',
            background: 'var(--border)', border: 'none', cursor: 'pointer',
            width: 16, height: 16, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--text-2)', fontSize: 10, padding: 0,
          }} title="Clear search">×</button>
        )}
      </div>
      <div style={{ maxHeight: 420, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)' }}>
        {rows.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--text-3)' }}>No purchases found.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                {([
                  ['Invoice No', 'left'], ['Date', 'left'], ['Company Name', 'left'],
                  ['Total Carat', 'right'], ['Total Amount', 'right'],
                ] as [string, 'left' | 'right'][]).map(([label, align]) => (
                  <th key={label} style={{
                    position: 'sticky', top: 0, zIndex: 2, background: 'var(--surface-2)',
                    padding: '8px 12px', fontSize: 10.5, fontWeight: 700, color: 'var(--text-3)',
                    textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: align,
                    borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
                  } as CSSProperties}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((inv, i) => (
                <tr key={inv.id} onClick={() => onPick(inv)}
                  style={{ cursor: 'pointer', background: i % 2 === 0 ? 'var(--surface)' : 'var(--surface-2)' }}>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }} title={inv.invoiceNo}><IdPill>{codeDisplay(inv.invoiceNo)}</IdPill></td>
                  <td style={{ padding: '9px 12px', fontFamily: 'var(--font-num)', color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{fmtDateDMY(inv.purchaseDate)}</td>
                  <td style={{ padding: '9px 12px', fontWeight: 600, color: 'var(--text-1)' }}>{entityName(inv.fromEntityId)}</td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: 'var(--font-num)', color: 'var(--text-2)' }}>{(inv.totalCarat ?? 0).toFixed(2)}</td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 600, color: 'var(--text-1)' }}>{money(inv.totalAmountUSD)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  );
}

export default function PurchaseReturnsPage() {
  const { business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);

  const [returns, setReturns]   = useState<PurchaseReturn[]>([]);
  const [invoices, setInvoices] = useState<PurchaseInvoice[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading]   = useState(true);

  const [dateKey, setDateKey] = useState<DateFilterKey>('this-month');
  const [searchQ, setSearchQ] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const [picking, setPicking]     = useState(false);
  const [returnFor, setReturnFor] = useState<PurchaseInvoice | null>(null);
  const [confirmDel, setConfirmDel] = useState<PurchaseReturn | null>(null);
  const [busy, setBusy] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    const u1 = onSnapshot(collection(db, 'purchaseReturns'),
      snap => { setReturns(snap.docs.map(d => ({ id: d.id, ...d.data() } as PurchaseReturn))); setLoading(false); },
      () => setLoading(false));
    const u2 = onSnapshot(collection(db, 'invoices'),
      snap => setInvoices(snap.docs.map(d => ({ id: d.id, ...d.data() } as PurchaseInvoice))), () => {});
    const u3 = onSnapshot(collection(db, 'entities'),
      snap => setEntities(snap.docs.map(d => ({ id: d.id, ...d.data() } as Entity))), () => {});
    return () => { u1(); u2(); u3(); };
  }, [business]);

  const entityName = (id: string) => entities.find(e => e.id === id)?.companyName ?? '—';
  const window = useMemo(() => computeDateWindow(dateKey, customFrom, customTo), [dateKey, customFrom, customTo]);
  const rows = useMemo(() => {
    if (!window) return [];
    const q = searchQ.trim().toLowerCase();
    return returns
      .filter(r => inWindow(r.date, window))
      .filter(r => !q || [r.returnNo, r.invoiceNo, entityName(r.fromEntityId)].some(v => (v ?? '').toLowerCase().includes(q)))
      .sort((a, b) => b.date.localeCompare(a.date));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returns, window, searchQ, entities]);
  const total = useMemo(() => rows.reduce((s, r) => s + returnCreditOf(r.totalCostUSD, r), 0), [rows]);

  useEffect(() => { setCurrentPage(1); }, [dateKey, customFrom, customTo, searchQ]);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pagedRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Print covers every filtered row, not just this page (same as the other list pages).
  // globalThis, not window: `window` here is the page's date range.
  const [printAll, setPrintAll] = useState(false);
  useEffect(() => {
    if (!printAll) return;
    let cancelled = false;
    const after = () => setPrintAll(false);
    globalThis.addEventListener('afterprint', after);
    requestAnimationFrame(() => { if (!cancelled) requestAnimationFrame(() => { if (!cancelled) globalThis.print(); }); });
    return () => { cancelled = true; globalThis.removeEventListener('afterprint', after); };
  }, [printAll]);

  function handleExportCSV() {
    downloadCSV('purchase-returns',
      ['Return No', 'Date', 'Supplier', 'Invoice No', 'Carat', 'Price Credit'],
      rows.map(r => [r.returnNo, r.date, entityName(r.fromEntityId), r.invoiceNo ?? '', (r.totalCarat ?? 0).toFixed(2), returnCreditOf(r.totalCostUSD, r).toFixed(2)]),
    );
  }

  async function doDelete(ret: PurchaseReturn) {
    setBusy(true);
    const db = getClientDb();
    if (!db || !business) { setBusy(false); return; }
    try {
      const invRef = doc(db, 'invoices', ret.invoiceId);
      // The "Returned to supplier" lines it wrote in the stones' history.
      const histRefs = await findReturnHistoryRefs(db, 'purchase', ret);
      await runTransaction(db, async tx => {
        const itemSnaps = await Promise.all(ret.lines.map(l => tx.get(doc(db, 'inventory', l.inventoryItemId))));
        const invSnap = await tx.get(invRef);

        ret.lines.forEach((l, i) => {
          const s = itemSnaps[i];
          if (!s.exists()) return;   // item gone — best-effort
          const cur = s.data() as InventoryItem;
          const itRef = doc(db, 'inventory', l.inventoryItemId);
          if (l.wholeItem) {
            tx.update(itRef, stripUndef({
              carat: l.carat, costUSD: l.costUSD, costingUSD: l.costingUSD,
              status: 'in-stock', returnedDate: undefined, returnReason: undefined,
              updatedAt: serverTimestamp(),
            }));
          } else {
            tx.update(itRef, {
              carat: round2((cur.carat ?? 0) + l.carat),
              costUSD: round2((cur.costUSD ?? 0) + l.costUSD),
              costingUSD: round2((cur.costingUSD ?? 0) + l.costingUSD),
              updatedAt: serverTimestamp(),
            });
          }
        });

        if (invSnap.exists()) {
          const inv = invSnap.data() as PurchaseInvoice;
          const newCredited = round2(Math.max(0, (inv.creditedUSD ?? 0) - returnCreditOf(ret.totalCostUSD, ret)));
          const settled = (inv.paidUSD ?? 0) + newCredited;
          const newStatus: PaymentStatus =
            settled >= inv.totalAmountUSD - 0.005 ? 'paid' : settled > 0 ? 'partial' : 'unpaid';
          tx.update(invRef, { creditedUSD: newCredited, paymentStatus: newStatus, updatedAt: serverTimestamp() });
        }

        tx.delete(doc(db, 'purchaseReturns', ret.id));
        histRefs.forEach(r => tx.delete(r));
        tx.delete(doc(db, 'journalEntries', entryId('purchase_return', ret.id)));
      });
    } finally { setBusy(false); setConfirmDel(null); }
  }

  const supplierName = returnFor ? entityName(returnFor.fromEntityId) : '';

  return (
    <AppLayout
      title="Purchase Returns"
      subtitle="Goods sent back to a supplier — reverses stock and reduces what you owe them."
      toolbar={<>
        <HeaderSearch value={searchQ} onChange={setSearchQ} placeholder="Return no, invoice no, supplier…" label="Search returns" />
        <DateRangeButton
          value={dateKey} onChange={setDateKey}
          customFrom={customFrom} customTo={customTo}
          onCustomFrom={setCustomFrom} onCustomTo={setCustomTo}
        />
        {(searchQ || dateKey !== 'this-month') && (
          <button type="button" className="tb-clear" onClick={() => { setSearchQ(''); setDateKey('this-month'); }}>Clear</button>
        )}
      </>}
      headerActions={<>
        <div className="tb-group">
          <Dropdown
            align="right"
            trigger={() => (
              <button type="button" className="tb-group-btn" title="Export" aria-label="Export"><IconExportSm /></button>
            )}
          >
            <button type="button" className="dropdown-item" onClick={handleExportCSV}>Export CSV</button>
            <button type="button" className="dropdown-item" onClick={() => setPrintAll(true)}>Print List</button>
          </Dropdown>
        </div>
        <Button variant="primary" size="sm" icon={<IconPlusSm />} onClick={() => setPicking(true)} disabled={!business}
          style={{ height: 34, boxSizing: 'border-box' }}>New Return</Button>
      </>}
    >
      <ReturnHistoryCleanup kind="purchase" returns={returns} loading={loading} />
      <StatChips items={[
        { label: 'Returned', value: money(total), tone: 'primary' },
        { label: 'Returns', value: String(rows.length) },
      ]} />

      {loading ? (
        <div className="card"><div className="skeleton" style={{ height: 240, borderRadius: 8 }} /></div>
      ) : rows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>No returns here</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Use "New Return" to send goods back to a supplier.</div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Return No</th>
                  <th style={{ width: 110 }}>Date</th>
                  <th>Supplier</th>
                  <th style={{ width: 110 }}>Invoice No</th>
                  <th style={{ textAlign: 'right', width: 90 }}>Carat</th>
                  <th style={{ textAlign: 'right', width: 120 }}>Price Credit</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(printAll ? rows : pagedRows).map(r => (
                  <tr key={r.id}>
                    <td title={r.returnNo}><IdPill>{codeDisplay(r.returnNo)}</IdPill></td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{fmtDate(r.date)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{entityName(r.fromEntityId)}</td>
                    <td title={r.invoiceNo}><IdPill>{codeDisplay(r.invoiceNo)}</IdPill></td>
                    <td style={{ textAlign: 'right', fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{(r.totalCarat ?? 0).toFixed(2)}</td>
                    <td style={{ textAlign: 'right', fontSize: 12, color: 'var(--text-1)', fontFamily: 'var(--font-num)', fontWeight: 600 }}>{money(returnCreditOf(r.totalCostUSD, r))}</td>
                    <td className="col-actions">
                      <Button variant="danger" size="sm" icon={<IconTrash />} title="Delete return"
                        onClick={() => setConfirmDel(r)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <Pagination
          current={currentPage} total={totalPages}
          totalItems={rows.length} pageSize={PAGE_SIZE}
          onChange={setCurrentPage}
        />
      )}

      {picking && (
        <PickInvoiceModal
          invoices={invoices} entityName={entityName}
          onPick={inv => { setPicking(false); setReturnFor(inv); }}
          onClose={() => setPicking(false)}
        />
      )}
      {returnFor && (
        <PurchaseReturnModal invoice={returnFor} supplierName={supplierName} onClose={() => setReturnFor(null)} />
      )}
      {confirmDel && (
        <ConfirmModal
          open title="Delete this return?"
          message={`${confirmDel.returnNo} will be undone — ${(confirmDel.totalCarat ?? 0).toFixed(2)} ct goes back into stock and ${money(returnCreditOf(confirmDel.totalCostUSD, confirmDel))} is added back to what you owe.`}
          confirmLabel="Delete" loading={busy}
          onConfirm={() => doDelete(confirmDel)} onClose={() => setConfirmDel(null)}
        />
      )}
    </AppLayout>
  );
}
